/**
 * Infinite looping carousel for the Dog reviews section.
 * Shows a fixed number of cards per view (3 desktop / 1 mobile via CSS) and
 * loops seamlessly by moving the leading/trailing card to the other end after
 * each animated step.
 */
class DogReviewsCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('[data-carousel-track]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.isAnimating = false;
    this.autoplayTimer = null;
    this.autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
  }

  connectedCallback() {
    if (!this.track || this.track.children.length < 2) return;

    this.prevButton?.addEventListener('click', () => {
      this.prev();
      this.restartAutoplay();
    });
    this.nextButton?.addEventListener('click', () => {
      this.next();
      this.restartAutoplay();
    });

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.stopAutoplay() : this.startAutoplay();
    });

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  get step() {
    const first = this.track.firstElementChild;
    if (!first) return 0;
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  next() {
    if (this.isAnimating) return;
    this.isAnimating = true;
    const distance = this.step;

    if (this.reduceMotion) {
      this.track.appendChild(this.track.firstElementChild);
      this.isAnimating = false;
      return;
    }

    this.track.style.transition = 'transform 0.3s ease';
    this.track.style.transform = `translateX(-${distance}px)`;
    this.track.addEventListener(
      'transitionend',
      () => {
        this.track.style.transition = 'none';
        this.track.appendChild(this.track.firstElementChild);
        this.track.style.transform = 'translateX(0)';
        // Force reflow so the next transition starts from the reset position.
        void this.track.offsetHeight;
        this.isAnimating = false;
      },
      { once: true }
    );
  }

  prev() {
    if (this.isAnimating) return;
    this.isAnimating = true;
    const distance = this.step;

    if (this.reduceMotion) {
      this.track.insertBefore(this.track.lastElementChild, this.track.firstElementChild);
      this.isAnimating = false;
      return;
    }

    this.track.style.transition = 'none';
    this.track.insertBefore(this.track.lastElementChild, this.track.firstElementChild);
    this.track.style.transform = `translateX(-${distance}px)`;
    void this.track.offsetHeight;
    this.track.style.transition = 'transform 0.3s ease';
    this.track.style.transform = 'translateX(0)';
    this.track.addEventListener(
      'transitionend',
      () => {
        this.isAnimating = false;
      },
      { once: true }
    );
  }

  startAutoplay() {
    if (this.reduceMotion || this.autoplayTimer || this.autoplayDelay <= 0) return;
    this.autoplayTimer = window.setInterval(this.next, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

customElements.define('dog-reviews-carousel', DogReviewsCarousel);
